/**
 * Parser client-side de QR de NFC-e.
 * Extrai chave de acesso (chNFe / 44 dígitos) e valor total (vNF) da URL,
 * sem chamada de rede. Formatos variam por UF — campos ausentes ficam undefined.
 */

export interface NfceQrParseResult {
  kind: 'nfce';
  rawUrl: string;
  accessKey?: string;
  totalValue?: number;
}

export interface CustomerQrParseResult {
  kind: 'customer';
  /** CPF/CNPJ ou uniqueId cru. */
  uniqueId: string;
}

export type QrParseResult = NfceQrParseResult | CustomerQrParseResult | { kind: 'unknown'; raw: string };

const ACCESS_KEY_RE = /\b(\d{44})\b/;
const CHNFE_RE = /[?&#]chNFe=(\d{44})/i;
const VNF_RE = /[?&#](?:vNF|total|valor)=([0-9]+(?:[.,][0-9]+)?)/i;

function parseBrNumber(raw: string): number | undefined {
  const normalized = raw.includes(',') ? raw.replace(/\./g, '').replace(',', '.') : raw;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : undefined;
}

function looksLikeUrl(text: string): boolean {
  return /^https?:\/\//i.test(text.trim()) || /nfce|nfe\.fazenda|sefaz/i.test(text);
}

function digitsOnly(text: string): string {
  return text.replace(/\D/g, '');
}

/**
 * Classifica o conteúdo do QR: URL de nota → NFC-e; CPF/CNPJ puro → cliente.
 */
export function parseOperatorQr(raw: string): QrParseResult {
  const text = raw.trim();
  if (!text) return { kind: 'unknown', raw };

  if (looksLikeUrl(text)) {
    let accessKey: string | undefined;
    const ch = text.match(CHNFE_RE);
    if (ch) accessKey = ch[1];
    else {
      const m = text.match(ACCESS_KEY_RE);
      if (m) accessKey = m[1];
    }

    let totalValue: number | undefined;
    const vnf = text.match(VNF_RE);
    if (vnf) totalValue = parseBrNumber(vnf[1]);

    // Alguns estados embutem p=BASE64… — tentativa best-effort de achar 44 dígitos no query
    if (!accessKey) {
      try {
        const url = new URL(text.startsWith('http') ? text : `https://x/${text}`);
        for (const [, val] of url.searchParams) {
          const d = digitsOnly(val);
          if (d.length === 44) accessKey = d;
          if (/^\d+([.,]\d+)?$/.test(val) && !totalValue) {
            const n = parseBrNumber(val);
            if (n != null && n > 0 && n < 1_000_000) totalValue = n;
          }
        }
      } catch {
        /* URL inválida — ainda assim devolvemos raw */
      }
    }

    return { kind: 'nfce', rawUrl: text, accessKey, totalValue };
  }

  const digits = digitsOnly(text);
  // CPF (11) ou CNPJ (14) — ou uniqueId alfanumérico curto do wallet
  if (digits.length === 11 || digits.length === 14) {
    return { kind: 'customer', uniqueId: digits };
  }
  if (/^[A-Za-z0-9._-]{4,64}$/.test(text) && !/\s/.test(text)) {
    return { kind: 'customer', uniqueId: text };
  }

  return { kind: 'unknown', raw: text };
}
